import type { OptionTrade } from '../../services/optionTradesService';
import type { WiseguyAlert } from '../../types/wiseguy';

/**
 * Adapts FZN alerts onto the row shape the Overview's flow tables render.
 *
 * The two flow panels moved from `/v1/option-trades` (the raw per-print tape)
 * onto `/v1/steamroom/tickers/{symbol}/alerts` (the floored FZN alert feed).
 * `FlowTable` and its nine columns are unchanged; only the rows feeding them
 * are different, so the mapping lives here rather than reshaping the table.
 *
 * ⚠️ **The two shapes disagree in ways that fail silently.** `bias` differs in
 * case, `strike` is a string on one side and a number on the other, and the
 * alert's `id` is a hash that collides. Each is handled below with a note on
 * what breaks without it.
 *
 * Shipped in PR #122 (repoint) and #123 (explicit window + bearer-stampede
 * fix); the spec doc has been deleted now that the behaviour lives here.
 */

/** Alerts with no usable numeric value render as a dash, never as zero. */
function num(v: string | number | null | undefined): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * ⚠️ **There is deliberately no multi-leg filter here.** An earlier version
 * dropped rows with `n_legs > 1`, on the assumption that `n_legs` counts
 * strategy legs. It does not — it is the **fill/execution count**. A real NVDA
 * MULTI-SWEEP arrives as `n_legs: 32, n_venues: 16` with a single `strike`,
 * `expiry` and `contract_type`, and `combo_id`/`combo_n_legs`/`complex` all
 * null-or-false: one order, one contract, thirty-two fills across sixteen
 * venues.
 *
 * That filter therefore removed every sweep — the most interesting flow on the
 * tape — and the panels rendered empty on active tickers. Nothing else in the
 * app filters on `n_legs`; the Wiseguy Alerts tool shows these rows.
 *
 * The genuine combo signals are `complex` / `combo_id` / `combo_n_legs`, and
 * `adaptFznAlert` does not carry them onto `WiseguyAlert` — so filtering on
 * them would mean threading the fields through the adapter first. Worth doing
 * only if combos actually prove to be a problem in these tables.
 */

/**
 * One alert as a flow-table row.
 *
 * Returns `null` when the alert cannot be rendered — see `strike` below.
 * Callers drop nulls rather than substituting a placeholder row.
 */
export function alertToFlowRow(alert: WiseguyAlert): OptionTrade | null {
  // ⚠️ `strike` is a string on the alert and a number on the row, and
  // `FlowTable` calls `.toFixed(2)` on it unguarded. A non-numeric strike would
  // throw and blank the whole panel, so the row is dropped instead.
  const strike = num(alert.strike);
  if (strike == null) return null;

  // ⚠️ Identity is `external_id`, never `alert.id`. That id is an FNV-1a hash
  // synthesized for display and it collides at real volume — it has already
  // silently dropped distinct alerts elsewhere. It is the React key here, so a
  // collision means duplicate keys. `external_id` is nullable, so fall back to
  // a composite that is still unique per contract per timestamp.
  const id =
    alert.external_id ??
    `${alert.received_on}:${alert.contract_type}:${alert.strike}:${alert.expiry}`;

  return {
    id,
    ts: alert.received_on,

    // ⚠️ Lowercased. Alerts capitalise (`Bullish`); `FlowTable` branches on the
    // lowercase form, so passing it through renders every row grey.
    bias: alert.bias.toLowerCase() as OptionTrade['bias'],

    // Already `Call` / `Put` — the same long form the table expects.
    option_type: alert.contract_type,

    underlying_price: num(alert.reference_price),
    strike,

    // The alert's premium. Feeds both the Premium column and the Largest
    // Alerts ranking.
    premium: alert.bet,

    expiry: alert.expiry,

    // ⚠️ Client-computed by `adaptFznAlert` and floored at 0, not
    // server-computed as on the tape. Optional there, so default rather than
    // rendering `undefined d`.
    dte: alert.time_to_expiry_days ?? 0,

    // Contract count. Not rendered by `FlowTable` today, but non-nullable on
    // the row shape.
    size: alert.qty ?? 0,
    price: num(alert.last_price) ?? 0,

    // ⚠️ Not the tape's ask/bid/mid, and not rendered. Left at `mid` rather
    // than guessed from `side_descriptor` — and never used to derive `bias`,
    // which is its own independent signal.
    side: 'mid',

    exchange: alert.venue_tag ?? '',
    open_interest: alert.open_interest ?? null,
    implied_volatility: alert.iv ?? null,

    // Comma-delimited on alerts (`AUCTION,OPENING`) rather than a single tape
    // code. Rendered verbatim; empty renders as a dash.
    condition: alert.condition ?? '',
  };
}

/**
 * Alerts as flow rows, newest first.
 *
 * The feed is already time-ordered; this only maps and drops what cannot be
 * rendered.
 */
export function toRecentRows(alerts: WiseguyAlert[]): OptionTrade[] {
  return alerts.map(alertToFlowRow).filter((r): r is OptionTrade => r !== null);
}

/**
 * Alerts as flow rows, largest premium first.
 *
 * ⚠️ **Ranked client-side, and deliberately so.** The alert route has no `sort`
 * parameter — unlike `/v1/option-trades`, which ranked server-side and where a
 * client sort was correctly removed. There is no server ranking to prefer here,
 * so the choice is this or no ranking at all.
 *
 * ⚠️ **Rank the whole window, then slice.** Callers must fetch the full window
 * rather than a short page: ranking a page of the most recent alerts yields
 * "the largest of the last few", under a heading that says Largest.
 *
 * ⚠️ **Slicing happens after the sort**, never before.
 */
export function toLargestRows(alerts: WiseguyAlert[], limit: number): OptionTrade[] {
  return toRecentRows(alerts)
    .slice()
    .sort((a, b) => (b.premium ?? 0) - (a.premium ?? 0))
    .slice(0, limit);
}
